import Big from 'big.js';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { RoundingType } from 'api';

interface FormikType {
  quantity: number;
  price_per_share: number;
  total: number;
  fee: number;
}

interface UseQuantityPricePerShareTotalProps<T extends FormikType> {
  formik: ReturnType<typeof useFormik<T>>;
  allowsFractionalSharesQuantity: boolean;
  roundingTypeQuantity?: RoundingType;
  addFeeToTotal?: boolean;
  decimalPointsQuantity?: number;
  decimalPointsTotal?: number;
  updateTotalForTrailingDecimal?: boolean;
}

type useQuantityPricePerShareTotal<T extends FormikType> = (
  props: UseQuantityPricePerShareTotalProps<T>,
) => {
  onChangeQuantity: (quantity: string | number) => void;
  onChangeTotal: (total: string | number) => void;
  onChangePricePerShare: (pricePerShare: string | number) => void;
  onChangeFee: (fee: string | number) => void;
};

export const useQuantityPricePerShareTotal: useQuantityPricePerShareTotal<any & FormikType> = ({
  formik,
  allowsFractionalSharesQuantity = true,
  roundingTypeQuantity = RoundingType.Up,
  addFeeToTotal = false,
  decimalPointsQuantity = 2,
  decimalPointsTotal = 2,
  updateTotalForTrailingDecimal = false,
}) => {
  const handleChangeTotal = useCallback(
    async (total: number | string) => {
      if (!formik.values.price_per_share) {
        await formik.setFieldValue('price_per_share', 1);
      }
      formik.setFieldValue('total', total);

      let quantity = new Big(total || 1)
        .minus(addFeeToTotal && formik.values.fee ? formik.values.fee : 0)
        .div(formik.values.price_per_share || 1);

      if (!allowsFractionalSharesQuantity) {
        if (roundingTypeQuantity === RoundingType.Up) {
          quantity = quantity.round(0, BigRoundMode.roundUp);
        } else {
          quantity = quantity.round(0, BigRoundMode.roundDown);
        }
      } else {
        quantity = quantity.round(decimalPointsQuantity, BigRoundMode.roundHalfUp);
      }

      formik.setFieldValue('quantity', Number(quantity.valueOf()));

      formik.setFieldError('price_per_share', '');
      formik.setFieldError('quantity', '');
      formik.setFieldError('total', '');
    },
    [
      formik,
      addFeeToTotal,
      allowsFractionalSharesQuantity,
      roundingTypeQuantity,
      decimalPointsQuantity,
    ],
  );

  const handleChangeQuantity = useCallback(
    (quantity: string | number) => {
      formik.setFieldError('quantity', '');
      formik.setFieldError('total', '');

      if (quantity === '') {
        formik.setFieldValue('quantity', '');
        formik.setFieldValue('total', '');
        return;
      }

      let value = String(quantity || '');

      //We remove all the characters, except for numbers, commas and points
      value = value.replace(/[^\d.,]/g, '');

      //We allow only one point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      //Comma processing
      const hasDot = value.includes('.');
      if (hasDot) {
        // If there is a point, the commas are just deleted
        value = value.replace(/,/g, '');
      } else {
        // if there is no point, but there is one comma, replace it with a point
        const commas = value.match(/,/g)?.length || 0;
        if (commas === 1) {
          value = value.replace(',', '.');
        } else {
          // several commas - we delete everything
          value = value.replace(/,/g, '');
        }
      }

      const regex = /^[0-9.,]*$/;

      if (!regex.test(value)) {
        return;
      }

      value = value.replaceAll(',', '.').replaceAll('..', '.');
      if (value === '.') {
        return;
      }
      if (hasDot) {
        // const frac = value.split('.')?.[1] || '';
        if (
          value.endsWith('.') //|| frac.length <= decimalPointsQuantity
        ) {
          formik.setFieldValue('quantity', value);
          if (updateTotalForTrailingDecimal) {
            const quantityWithoutTrailingDecimal = new Big(value.slice(0, -1) || 0);
            formik.setFieldValue(
              'total',
              quantityWithoutTrailingDecimal && formik.values.price_per_share
                ? Number(
                    quantityWithoutTrailingDecimal
                      .mul(formik.values.price_per_share)
                      .plus(addFeeToTotal && formik.values.fee ? formik.values.fee : 0)
                      .round(decimalPointsTotal, BigRoundMode.roundHalfUp)
                      .valueOf(),
                  )
                : 0,
            );
          }
          return;
        }
      }

      let num = new Big(value);
      if (allowsFractionalSharesQuantity) {
        num = num.round(decimalPointsQuantity, BigRoundMode.roundHalfUp);
      } else {
        num =
          roundingTypeQuantity === RoundingType.Up
            ? num.round(0, BigRoundMode.roundUp)
            : num.round(0, BigRoundMode.roundDown);
      }

      formik.setFieldValue('quantity', Number(num.valueOf()));
      formik.setFieldValue(
        'total',
        num && formik.values.price_per_share
          ? Number(
              new Big(num)
                .mul(formik.values.price_per_share)
                .plus(addFeeToTotal && formik.values.fee ? formik.values.fee : 0)
                .round(decimalPointsTotal, BigRoundMode.roundHalfUp)
                .valueOf(),
            )
          : 0,
      );
    },
    [
      decimalPointsQuantity,
      decimalPointsTotal,
      formik.values.fee,
      formik.values.price_per_share,
      addFeeToTotal,
      updateTotalForTrailingDecimal,
      allowsFractionalSharesQuantity,
      roundingTypeQuantity,
    ],
  );

  const handleChangePricePerShare = useCallback(
    (pricePerShare: number | string) => {
      formik.setFieldValue('price_per_share', pricePerShare);
      formik.setFieldValue(
        'total',
        pricePerShare && formik.values.quantity
          ? Number(
              new Big(formik.values.quantity)
                .mul(pricePerShare)
                .plus(addFeeToTotal && formik.values.fee ? formik.values.fee : 0)
                .round(decimalPointsTotal, BigRoundMode.roundHalfUp)
                .valueOf(),
            )
          : 0,
      );

      formik.setFieldError('price_per_share', '');
      formik.setFieldError('total', '');
    },
    [formik.values.fee, addFeeToTotal, formik.values.quantity, decimalPointsTotal],
  );

  const handleChangeFee = useCallback(
    (fee: number | string) => {
      formik.setFieldValue('fee', fee);
      formik.setFieldError('fee', '');
      const total =
        formik.values.price_per_share && formik.values.quantity
          ? Number(
              new Big(formik.values.quantity)
                .mul(formik.values.price_per_share)
                .plus(fee ? fee : 0)
                .round(decimalPointsTotal, BigRoundMode.roundHalfUp)
                .valueOf(),
            )
          : 0;
      formik.setFieldValue('total', total);
      formik.setFieldError('total', '');
    },
    [formik.values.price_per_share, formik.values.quantity, decimalPointsTotal],
  );

  return {
    onChangeQuantity: handleChangeQuantity,
    onChangeTotal: handleChangeTotal,
    onChangePricePerShare: handleChangePricePerShare,
    onChangeFee: handleChangeFee,
  };
};
